// src/components/Department/DepartmentModal.jsx
import React, { useState, useEffect } from 'react';
import { showNotification } from '../../utils/notification';
import { alertNotification } from '../../utils/notification';

/**
 * DepartmentModal Component
 *
 * @param {boolean} isOpen
 * @param {Function} onClose
 * @param {Function} onSave
 * @param {Object} department (optional - edit mode)
 * @param {boolean} isSaving
 */
const DepartmentModal = ({ isOpen, onClose, onSave, department, isSaving }) => {
  const MAX_ITEMS = 5;

  // Form state
  const [departmentName, setDepartmentName] = useState('');
  const [isActive, setIsActive] = useState(true);

  // Table data
  const [departments, setDepartments] = useState([]);

  // Populate data when editing (optional support)
  useEffect(() => {
    if (department) {
      setDepartments([
        {
          name: department.name || '',
          isActive: department.isActive ?? true,
        },
      ]);
    } else {
      setDepartments([]);
    }

    setDepartmentName('');
    setIsActive(true);
  }, [department, isOpen]);

  // Add department to table
  const addRow = () => {
    if (!departmentName.trim()) {
      alert('Department name is required');
      return;
    }

    if (departments.length >= MAX_ITEMS) {
      alert('Maximum 5 departments allowed');
      return;
    }

    // Duplicate check (case-insensitive)
    const exists = departments.some(
      (d) => d.name.toLowerCase() === departmentName.trim().toLowerCase()
    );

    if (exists) {
      alertNotification('Department already exists', 'error');
      return;
    }

    setDepartments((prev) => [
      ...prev,
      { name: departmentName.trim(), isActive },
    ]);

    // Clear form
    setDepartmentName('');
    setIsActive(true);
  };

  // Remove department
  const removeRow = (index) => {
    setDepartments((prev) => prev.filter((_, i) => i !== index));
  };

  // Save departments
  const handleSubmit = (e) => {
    e.preventDefault();

    if (departments.length === 0) {
      alert('Please add at least one department');
      return;
    }

    onSave({ departments });
  };

  const handleClose = () => {
    if (!isSaving) onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="inventory-modal-overlay">
      <div className="inventory-modal-content">
        {/* Header */}
        <header className="inventory-modal-header">
          <h3 className="inventory-modal-title">
            {department ? 'Edit Department' : 'Add New Department'}
          </h3>
          <button
            className="inventory-close-modal-btn"
            onClick={handleClose}
            disabled={isSaving}
            type="button"
          >
            ×
          </button>
        </header>

        {/* Body */}
        <div className="inventory-modal-body">
          <form onSubmit={handleSubmit}>
            {/* FORM INPUTS */}
            <div className="inventory-form-grid">
              <label>
                Department Name
                <span style={{ color: 'red' }}> *</span>
              </label>
              <input
                type="text"
                className="inventory-custom-input"
                value={departmentName}
                onChange={(e) => setDepartmentName(e.target.value)}
                disabled={isSaving}
                placeholder="e.g., HR, Finance, IT"
              />

              <label>Active</label>
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled={isSaving}
              />

              <button
                type="button"
                className="inventory-btn inventory-btn-action inventory-btn-edit"
                onClick={addRow}
                disabled={departments.length >= MAX_ITEMS || isSaving}
              >
                Add
              </button>
            </div>

            {/* TABLE */}
            <table className="inventory-data-table" style={{ marginTop: 16 }}>
              <thead>
                <tr>
                  <th>Department Name</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {departments.length === 0 ? (
                  <tr>
                    <td colSpan="3">No data</td>
                  </tr>
                ) : (
                  departments.map((dept, index) => (
                    <tr key={index}>
                      <td>{dept.name}</td>
                      <td>{dept.isActive ? 'Active' : 'Inactive'}</td>
                      <td>
                        <button
                          type="button"
                          onClick={() => removeRow(index)}
                          disabled={isSaving}
                        >
                           ❌
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </form>
        </div>

        {/* Footer */}
        <footer className="inventory-modal-footer">
          <button
            className="inventory-btn inventory-btn-cancel"
            onClick={handleClose}
            disabled={isSaving}
            type="button"
          >
            Cancel
          </button>
          <button
            className="inventory-btn inventory-btn-save"
            onClick={handleSubmit}
            disabled={isSaving}
            type="button"
          >
            {isSaving ? 'Saving...' : 'Save Department'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default DepartmentModal;
